import { createContext, type ReactNode, useContext, useMemo } from "react";

import { useAuthStore } from "../auth-store";

export interface ApiConfig {
  baseUrl: string;
}

export type AuthStrategy =
  | { type: "cookie" }
  | { type: "bearer"; getToken: () => string | null };

const ApiConfigContext = createContext<ApiConfig | null>(null);

export interface ApiConfigProviderProps {
  baseUrl: string;
  children: ReactNode;
}

export function ApiConfigProvider({
  baseUrl,
  children,
}: ApiConfigProviderProps) {
  const value = useMemo(() => ({ baseUrl }), [baseUrl]);
  return (
    <ApiConfigContext.Provider value={value}>
      {children}
    </ApiConfigContext.Provider>
  );
}

export function useApiConfig(): ApiConfig {
  const config = useContext(ApiConfigContext);
  if (!config) {
    throw new Error("useApiConfig must be used within an ApiConfigProvider");
  }
  return config;
}

export function useAuthStrategy(): AuthStrategy {
  const mode = useAuthStore((s) => s.mode);
  const token = useAuthStore((s) => s.token);

  return useMemo((): AuthStrategy => {
    if (mode === "bearer") {
      return {
        type: "bearer",
        getToken: () => token,
      };
    }
    return { type: "cookie" };
  }, [mode, token]);
}
